<template>
  <div class="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200">
    <div class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-space-y-0 tw-pb-2 tw-p-6">
      <div class="tw-flex tw-items-center tw-gap-2">
        <ShoppingCart class="tw-w-5 tw-h-5 tw-text-gray-600" />
        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900">
          {{ t('customer_management.titles.forms') }}
        </h3>
      </div>
    </div>

    <div class="tw-px-6 tw-pb-6">
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8">
        <!-- Order Forms -->
        <div>
          <h4 class="tw-mb-4 tw-text-base tw-font-medium tw-text-gray-900">
            {{ t('customer_management.titles.order_forms') }}
          </h4>
          <div class="tw-space-y-4">
            <div>
              <label class="tw-text-sm tw-text-gray-600">
                {{ t('customer_management.titles.mandatory_fields_hint') }}
              </label>
              <div class="tw-flex tw-flex-wrap tw-gap-2 tw-mt-2">
                <Badge v-for="field in forms?.orderRequired || []" :key="field" variant="default">
                  {{ formatFieldName(field) }}
                </Badge>
                <span v-if="!forms?.orderRequired?.length" class="tw-text-sm tw-text-gray-500">
                  No required fields configured
                </span>
              </div>
            </div>
            <div>
              <label class="tw-text-sm tw-text-gray-600">
                {{ t('customer_management.titles.optional_fields_hint') }}
              </label>
              <div class="tw-flex tw-flex-wrap tw-gap-2 tw-mt-2">
                <Badge v-for="field in forms?.orderOptional || []" :key="field" variant="secondary">
                  {{ formatFieldName(field) }}
                </Badge>
                <span v-if="!forms?.orderOptional?.length" class="tw-text-sm tw-text-gray-500">
                  No optional fields configured
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Forms -->
        <div>
          <h4 class="tw-mb-4 tw-text-base tw-font-medium tw-text-gray-900">
            {{ t('customer_management.titles.payment_form') }}
          </h4>
          <div class="tw-space-y-4">
            <div>
              <label class="tw-text-sm tw-text-gray-600">
                {{ t('customer_management.titles.mandatory_fields_hint') }}
              </label>
              <div class="tw-flex tw-flex-wrap tw-gap-2 tw-mt-2">
                <Badge v-for="field in forms?.paymentRequired || []" :key="field" variant="default">
                  {{ formatFieldName(field) }}
                </Badge>
                <span v-if="!forms?.paymentRequired?.length" class="tw-text-sm tw-text-gray-500">
                  No required fields configured
                </span>
              </div>
            </div>
            <div>
              <label class="tw-text-sm tw-text-gray-600">
                {{ t('customer_management.titles.optional_fields_hint') }}
              </label>
              <div class="tw-flex tw-flex-wrap tw-gap-2 tw-mt-2">
                <Badge
                  v-for="field in forms?.paymentOptional || []"
                  :key="field"
                  variant="secondary"
                >
                  {{ formatFieldName(field) }}
                </Badge>
                <span v-if="!forms?.paymentOptional?.length" class="tw-text-sm tw-text-gray-500">
                  No optional fields configured
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { ShoppingCart } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { Badge } from '@/components/ui/badge';
import { useCustomerStore } from '@/stores/customer.store';

interface CustomerForms {
  orderRequired?: string[];
  orderOptional?: string[];
  paymentRequired?: string[];
  paymentOptional?: string[];
}

interface Customer {
  forms?: CustomerForms;
  [key: string]: any; // Allow additional properties
}

interface Props {
  customer: Customer;
  customerId: number;
}

const props = defineProps<Props>();
const { t } = useI18n();
const { getFormOrderById, getFormPaymentById } = useCustomerStore();

// Local state for forms data
const formsData = ref<CustomerForms>({
  orderRequired: [],
  orderOptional: [],
  paymentRequired: [],
  paymentOptional: [],
});

const getFormsData = async () => {
  try {
    // Fetch order forms
    const orderFormsData = await getFormOrderById({
      customer: props.customerId,
    });
    const transformedOrderForms = {
      required: [] as string[],
      optional: [] as string[],
    };

    orderFormsData.results?.forEach((form: any) => {
      const fieldName = form.field.field_name;
      if (form.is_mandatory) {
        transformedOrderForms.required.push(fieldName);
      } else {
        transformedOrderForms.optional.push(fieldName);
      }
    });

    // Fetch payment forms
    const paymentFormsData = await getFormPaymentById({
      customer: props.customerId,
    });
    const transformedPaymentForms = {
      required: [] as string[],
      optional: [] as string[],
    };

    paymentFormsData.results?.forEach((form: any) => {
      const fieldName = form.field.field_name;
      if (form.is_mandatory) {
        transformedPaymentForms.required.push(fieldName);
      } else {
        transformedPaymentForms.optional.push(fieldName);
      }
    });

    formsData.value = {
      orderRequired: transformedOrderForms.required,
      orderOptional: transformedOrderForms.optional,
      paymentRequired: transformedPaymentForms.required,
      paymentOptional: transformedPaymentForms.optional,
    };
  } catch (error) {
    console.error('Error fetching forms data:', error);
  }
};

// Use forms data from props if available, otherwise use local data
const forms = computed(() => (props.customer as any).forms || formsData.value);

const formatFieldName = (field: string): string => {
  return field.replace(/-/g, ' ');
};

onMounted(async () => {
  // Only fetch if forms data is not provided via props
  if (!(props.customer as any).forms) {
    await getFormsData();
  }
});
</script>
